/**
 * Thin wrapper around `@capacitor/haptics` that no-ops on the web and on
 * Android (where heavy haptics aren't part of the user expectation).
 *
 * The native plugin is imported lazily so the web bundle doesn't pay for
 * its bytes — `import()` returns a stub when the platform isn't iOS, and
 * any errors (e.g. Capacitor missing in dev) are swallowed because
 * haptics are decorative, never functional.
 */
import { isIOS } from "./platform";

let cachedHaptics = null;

async function load() {
  if (!isIOS()) return null;
  if (cachedHaptics) return cachedHaptics;
  try {
    const mod = await import("@capacitor/haptics");
    cachedHaptics = mod;
    return mod;
  } catch {
    return null;
  }
}

/** A short positive confirmation — e.g. Vault unlocked, payment confirmed. */
export async function tapSuccess() {
  const h = await load();
  if (!h) return;
  try {
    await h.Haptics.notification({ type: h.NotificationType.Success });
  } catch {
    /* haptics are best-effort */
  }
}

/** A sharp warning — e.g. wrong PIN, payment failed. */
export async function tapError() {
  const h = await load();
  if (!h) return;
  try {
    await h.Haptics.notification({ type: h.NotificationType.Error });
  } catch {
    /* haptics are best-effort */
  }
}

/** A subtle "tick" — keypad buttons, picker steps. */
export async function tapLight() {
  const h = await load();
  if (!h) return;
  try {
    await h.Haptics.impact({ style: h.ImpactStyle.Light });
  } catch {
    /* haptics are best-effort */
  }
}
